import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../../services/api';

// Monthly performance coverage for every shadow portfolio code, grouped by suite and
// platform: missing months inside a portfolio's history, portfolios behind the latest
// month, portfolios with no performance at all, and registry inception dates that
// don't line up with the first return. Read-only; the data comes from the stored
// Peer_BM_Performance series (the same rules the Periods and Custom Period views use).

const FLAGS = {
  missing_months: { label: 'Missing months', cls: 'perfcov-b-missing' },
  lagging: { label: 'Behind', cls: 'perfcov-b-lag' },
  no_performance: { label: 'No performance', cls: 'perfcov-b-none' },
  inception_mismatch: { label: 'Inception mismatch', cls: 'perfcov-b-incep' },
};

const fmtMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};
const fmtDate = (iso) => (iso
  ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
  : '');

const PerformanceCoverage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState('suite');        // 'suite' (suite → platform) | 'platform'
  const [flagFilter, setFlagFilter] = useState('issues'); // 'issues' | 'all' | a FLAGS key
  const [platform, setPlatform] = useState('');
  const [suite, setSuite] = useState('');
  const [search, setSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [tailoredOnly, setTailoredOnly] = useState(false);

  useEffect(() => {
    adminAPI.getPerformanceCoverage()
      .then(setData)
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  const base = useMemo(() => (data?.portfolios || []).filter((p) => (!activeOnly || p.active)
    && (!tailoredOnly || p.tailored)), [data, activeOnly, tailoredOnly]);

  const counts = useMemo(() => {
    const c = { all: base.length, issues: 0, ok: 0 };
    Object.keys(FLAGS).forEach((k) => { c[k] = 0; });
    base.forEach((p) => {
      if (p.flags.length) c.issues += 1; else c.ok += 1;
      p.flags.forEach((f) => { c[f] += 1; });
    });
    return c;
  }, [base]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return base.filter((p) => {
      if (flagFilter === 'issues' && !p.flags.length) return false;
      if (FLAGS[flagFilter] && !p.flags.includes(flagFilter)) return false;
      if (platform && p.platform !== platform) return false;
      if (suite && p.suite !== suite) return false;
      if (q && !`${p.portfolio_code} ${p.name}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [base, flagFilter, platform, suite, search]);

  // Two-level grouping: suite → platform, or platform → suite.
  const groups = useMemo(() => {
    const outer = groupBy === 'suite' ? 'suite' : 'platform';
    const inner = groupBy === 'suite' ? 'platform' : 'suite';
    const g = {};
    rows.forEach((p) => {
      const o = p[outer] || 'Unknown';
      const i = p[inner] || 'Unknown';
      ((g[o] = g[o] || {})[i] = g[o][i] || []).push(p);
    });
    const last = (k) => (k === 'Unmapped' || k === 'Unknown' ? 1 : 0);
    return Object.entries(g)
      .sort(([a], [b]) => last(a) - last(b) || a.localeCompare(b))
      .map(([o, inners]) => [o, Object.entries(inners).sort(([a], [b]) => last(a) - last(b) || a.localeCompare(b))]);
  }, [rows, groupBy]);

  const platforms = useMemo(() => [...new Set(base.map((p) => p.platform))].sort(), [base]);
  const suites = useMemo(() => [...new Set(base.map((p) => p.suite))].sort(), [base]);

  if (error) return <div className="alert alert-danger">Coverage failed to load: {error}</div>;
  if (!data) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" role="status" />
        <span className="ms-2">Loading coverage…</span>
      </div>
    );
  }

  const tile = (key, label) => (
    <button
      type="button"
      className={`perfcov-tile ${flagFilter === key ? 'active' : ''} ${FLAGS[key]?.cls || ''}`}
      onClick={() => setFlagFilter(flagFilter === key && key !== 'issues' ? 'issues' : key)}
    >
      <div className="perfcov-tile-n">{counts[key]}</div>
      <div className="perfcov-tile-l">{label}</div>
    </button>
  );

  return (
    <div className="perfcov">
      <p className="text-muted small mb-3">
        Monthly performance available for each shadow portfolio code, measured against the latest
        reported month (<strong>{fmtMonth(data.latest_available_month)}</strong>
        {data.as_of_date ? `; portal as-of date ${fmtDate(data.as_of_date)}` : ''}). <em>Missing months</em> are
        months inside a portfolio&apos;s history with no platform report: any period that includes one is
        left blank on the Periods and Custom Period views. <em>Behind</em> means the latest reported month
        is earlier than the latest available. <em>Inception mismatch</em> means the first return is neither
        the registry inception month nor the month after it.
      </p>

      <div className="perfcov-tiles">
        {tile('issues', 'With an issue')}
        {tile('missing_months', FLAGS.missing_months.label)}
        {tile('lagging', FLAGS.lagging.label)}
        {tile('no_performance', FLAGS.no_performance.label)}
        {tile('inception_mismatch', FLAGS.inception_mismatch.label)}
        {tile('all', 'All portfolios')}
      </div>

      <div className="perfcov-controls">
        <label>Group by
          <select className="form-select form-select-sm" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="suite">Suite, then platform</option>
            <option value="platform">Platform, then suite</option>
          </select>
        </label>
        <label>Platform
          <select className="form-select form-select-sm" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">All</option>
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label>Suite
          <select className="form-select form-select-sm" value={suite} onChange={(e) => setSuite(e.target.value)}>
            <option value="">All</option>
            {suites.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <input
          className="form-control form-control-sm perfcov-search"
          placeholder="Search code or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="perfcov-check">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} /> Active only
        </label>
        <label className="perfcov-check">
          <input type="checkbox" checked={tailoredOnly} onChange={(e) => setTailoredOnly(e.target.checked)} /> Tailored only
        </label>
      </div>

      {groups.length === 0 && <div className="perfcov-empty">No portfolios match these filters.</div>}

      {groups.map(([outer, inners]) => {
        const n = inners.reduce((a, [, ps]) => a + ps.length, 0);
        return (
          <div className="card perfcov-group" key={outer}>
            <div className="card-header py-2 d-flex justify-content-between align-items-baseline">
              <h6 className="mb-0">{outer}</h6>
              <span className="text-muted small">{n} portfolio{n === 1 ? '' : 's'}</span>
            </div>
            <div className="card-body p-0">
              {inners.map(([inner, ps]) => (
                <div key={inner}>
                  <div className="perfcov-subhead">{inner} <span>· {ps.length}</span></div>
                  <div className="table-responsive">
                    <table className="perfcov-table">
                      <thead>
                        <tr>
                          <th>Code</th><th>Name</th><th>Inception</th><th>First return</th>
                          <th>Last reported</th><th className="r">Months</th><th>Missing months</th><th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ps.map((p) => (
                          <tr key={p.portfolio_code} className={p.active ? '' : 'perfcov-inactive'}>
                            <td className="perfcov-code">{p.portfolio_code}{p.tailored && <span className="perfcov-tag">tailored</span>}</td>
                            <td>{p.name}</td>
                            <td>{fmtDate(p.inception_date)}</td>
                            <td>{fmtMonth(p.first_month)}</td>
                            <td>
                              {fmtMonth(p.last_month)}
                              {p.months_behind > 0 && <span className="perfcov-behind"> ({p.months_behind} behind)</span>}
                            </td>
                            <td className="r">{p.months_reported || ''}</td>
                            <td className="perfcov-gaps">{p.gap_months.map(fmtMonth).join(', ')}</td>
                            <td>
                              {p.flags.length === 0
                                ? <span className="perfcov-badge perfcov-b-ok">OK</span>
                                : p.flags.map((f) => (
                                  <span key={f} className={`perfcov-badge ${FLAGS[f].cls}`}
                                        title={f === 'inception_mismatch' ? `First return ${p.inception_lead_months} months after inception month` : ''}>
                                    {FLAGS[f].label}
                                  </span>
                                ))}
                              {!p.show_performance && <span className="perfcov-badge perfcov-b-hidden">Hidden</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PerformanceCoverage;
